/**
 * The sidebar's panel kit: flat full-width rows split by dividers. PanelHeader is the shared
 * row (icon, title, trailing slot, optional full-width description); CollapsibleSection puts
 * a chevron in that slot and reveals its body under it.
 */

import { type ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Collapsible, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Reveal } from '../ui/reveal';

interface PanelHeaderProps {
  icon?: LucideIcon;
  title: string;
  description?: ReactNode;
  trailing?: ReactNode;
}

export const PanelHeader = ({ icon: Icon, title, description, trailing }: PanelHeaderProps) => (
  <div className='flex flex-col gap-1 px-4 py-3'>
    <div className='flex min-h-8.5 items-center gap-4'>
      <div className='flex min-w-0 flex-1 items-center gap-2'>
        {Icon && (
          <span className='flex size-5 shrink-0 items-center justify-center'>
            <Icon className='size-4 text-text-body' aria-hidden />
          </span>
        )}
        <p className='text-[13px] leading-4.5 font-medium text-text-body'>{title}</p>
      </div>
      {trailing}
    </div>
    {description && <div className='text-xs leading-4 text-gray-800'>{description}</div>}
  </div>
);

interface CollapsibleSectionProps {
  icon: LucideIcon;
  title: string;
  /** Open state is owned by the sidebar, so loading a scenario case can expand the sections it filled. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Something has been set up inside, so a folded section does not read as an empty one. */
  configured?: boolean;
  /** Just filled by a described world: lit for a moment so the eye catches it. */
  highlighted?: boolean;
  className?: string;
  children: ReactNode;
}

export const CollapsibleSection = ({
  icon,
  title,
  open,
  onOpenChange,
  configured = false,
  highlighted = false,
  className,
  children,
}: CollapsibleSectionProps) => (
  <Collapsible
    open={open}
    onOpenChange={onOpenChange}
    className={cn(
      'shrink-0 border-b border-gray-500 transition-colors duration-700',
      highlighted && 'bg-indigo-50/70',
      className
    )}
  >
    <CollapsibleTrigger className='w-full text-left transition-colors hover:bg-gray-100/60'>
      <PanelHeader
        icon={icon}
        title={title}
        trailing={
          <span className='flex shrink-0 items-center gap-1.5'>
            {configured && (
              <span
                // A hint, not a second control: the trigger already reads the section's name.
                title={`${title} has been configured`}
                className='size-1.5 rounded-full bg-amber-400'
              />
            )}
            <span className='flex size-7 items-center justify-center'>
              <ChevronRight
                strokeWidth={1.75}
                className={cn('size-3.5 text-text-body transition-transform duration-300', open && 'rotate-90')}
              />
            </span>
          </span>
        }
      />
    </CollapsibleTrigger>

    <Reveal open={open}>
      <div className='px-4 pb-4'>{children}</div>
    </Reveal>
  </Collapsible>
);

export const SectionEmpty = ({ icon: Icon, children }: { icon: LucideIcon; children: ReactNode }) => (
  <div className='flex items-center gap-2 text-xs leading-4 text-gray-800'>
    <Icon className='size-3.5 shrink-0' />
    <p>{children}</p>
  </div>
);
